import { CopyIcon, EditIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, Text, Tooltip } from '@chakra-ui/react'
import { getPlanNextAction } from '@hb/shared'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ProfileContext, useApp } from '../../contexts'
import { useMobileLayout } from '../../hooks/useMobileLayout'
import { Expandable } from '../Expandable'
import { AdditionalPlansView } from './AdditionalPlansView'
import { CoverageStage } from './Coverage'

const CopyLink = ({ link }: { link: string }) => {
  const [wasCopied, setWasCopied] = useState(false)
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(`https://${link}`)
    setWasCopied(true)
  }, [link])

  useEffect(() => {
    if (!wasCopied) return () => {}
    const t = setTimeout(() => setWasCopied(false), 3000)
    return () => {
      clearTimeout(t)
    }
  }, [wasCopied])

  return (
    <Tooltip isOpen={wasCopied} label="Copied!">
      <Button
        gap={1.5}
        variant="outline"
        size="sm"
        px={3}
        h={7}
        bg="white"
        borderRadius="full"
        onClick={handleCopy}>
        <Text>{link}</Text>
        <CopyIcon />
      </Button>
    </Tooltip>
  )
}
const ProvidersAppUpdateCoverage = ({ withAssessment }: { withAssessment?: boolean }) => (
  <Box w="100%" py={2} px={3}>
    <Flex
      border="1px solid"
      borderColor="gray.200"
      borderRadius={4}
      bg="gray.50"
      w="100%"
      align="center"
      px={5}
      py={2}
      gap={4}>
      <EditIcon w={7} h={7} color="green.600" />
      <Box fontSize="md">
        {withAssessment ? (
          <Text fontWeight={500} fontSize="lg" mr={1} as="span">
            This assessment has already been submitted.
          </Text>
        ) : null}
        <Text fontWeight={500} fontSize="lg" mr={1} as="span">
          If you would like to change an insurance plan on file, please ask your patient to schedule
          a call with HB at&nbsp;
        </Text>
        <CopyLink link="hamiltonbilling.com/contact" />
        <Text fontWeight={500} fontSize="md" opacity={0.8}>
          You can also message HB directly with the updates you would like to make, and the reason
          for making them.
        </Text>
      </Box>
    </Flex>
  </Box>
)

export const PatientPlansView = ({ preview }: { preview?: boolean }) => {
  const { user, patientRef } = useContext(ProfileContext)
  const { insurancePlans: plans } = user ?? {}
  const { appName } = useApp()
  const adminView = useMemo(() => appName === 'app', [appName])

  const { ref, width } = useMobileLayout()
  if (!patientRef) {
    return (
      <Flex flexFlow="column" p={3}>
        <Text>Select a patient to see plans</Text>
      </Flex>
    )
  }

  return (
    <Flex ref={ref} w="100%" py={preview ? 0 : 2} flexFlow="column">
      <Text px={3} fontSize="lg" lineHeight={1} fontWeight={600} color="gray.600">
        Patient Coverage
      </Text>
      <Flex w="100%" gap={preview ? 1 : 2} flexFlow="column" px={preview ? 1 : 3} py={2}>
        {plans?.primary ? (
          <CoverageStage
            id="primary"
            assessmentId={null}
            patientRef={patientRef}
            adminView={adminView}
            width={width}
            coverage={plans?.primary}
          />
        ) : null}
        {plans?.secondary ? (
          <CoverageStage
            id="secondary"
            assessmentId={null}
            patientRef={patientRef}
            adminView={adminView}
            width={width}
            coverage={plans.secondary}
          />
        ) : null}
      </Flex>
      <AdditionalPlansView
        adminView={adminView}
        assessment={null}
        assessmentId={null}
        patientRef={patientRef}
        preview={preview}
        user={user}
      />
      {appName === 'providers-app' ? <ProvidersAppUpdateCoverage /> : null}
    </Flex>
  )
}

export const PregnancyPlansView = () => {
  const { user, patientRef, assessmentId, selectedAssessment } = useContext(ProfileContext)
  // const { nextActions } = user ?? {}
  const { appName } = useApp()
  const adminView = useMemo(() => appName === 'app', [appName])
  const { plans, nextActions, submittedOn } = selectedAssessment.populated ?? {}
  const { primary, secondary } = plans ?? {}
  const { width } = useMobileLayout()
  const primaryNextAction =
    nextActions && primary ? getPlanNextAction(nextActions, primary.id) : null

  const secondaryNextAction =
    nextActions && secondary ? getPlanNextAction(nextActions, secondary.id) : null

  if (!assessmentId || !patientRef) {
    return (
      <Flex flexFlow="column" p={3}>
        <Text>Select a pregnancy to see plans</Text>
      </Flex>
    )
  }

  return (
    <Flex w="100%" py={2} flexFlow="column">
      <Text px={3} fontSize="xl" lineHeight={1} fontWeight={600} color="gray.600">
        Pregnancy Coverage
      </Text>
      <Flex w="100%" gap={2} flexFlow="column" px={3} py={2}>
        {primary ? (
          <CoverageStage
            id={primary.id}
            width={width}
            assessmentId={assessmentId}
            assessment={selectedAssessment.populated}
            patientRef={patientRef}
            nextAction={primaryNextAction}
            adminView={adminView}
            coverage={primary}
          />
        ) : (
          <Text px={2} py={1} fontSize="lg" lineHeight={1} color="gray.600" fontStyle="italic">
            No primary coverage
          </Text>
        )}
        {secondary ? (
          <CoverageStage
            id={secondary.id}
            assessment={selectedAssessment.populated}
            width={width}
            assessmentId={assessmentId}
            patientRef={patientRef}
            nextAction={secondaryNextAction}
            adminView={adminView}
            coverage={secondary}
          />
        ) : null}
      </Flex>
      <AdditionalPlansView
        assessmentId={assessmentId}
        assessment={selectedAssessment.populated}
        adminView={adminView}
        patientRef={patientRef}
        user={user}
      />
      {appName === 'providers-app' && submittedOn ? (
        <ProvidersAppUpdateCoverage withAssessment />
      ) : null}
    </Flex>
  )
}

export const CollapsiblePatientPlansView = () => (
  <Expandable
    borderRadius={6}
    headerProps={{ bg: 'gray.50', borderRadius: 4 }}
    border="1px solid #cdcdcd"
    header={() => (
      <Flex py={1} px={2} align="center" justify="space-between" w="100%">
        <Text fontSize="md" color="gray.600" fontWeight={500}>
          Insurance Plans
        </Text>
      </Flex>
    )}>
    <Box px={1} w="100%">
      <PatientPlansView preview />
    </Box>
  </Expandable>
)
